import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Textarea from '../../common/Textarea';
import Button from '../../common/Button';
import FileUpload from '../../common/FileUpload';
import { TICKET_PRIORITIES, TICKET_TYPES, TYPES_REQUIRING_NO_SELECTION, TICKET_TYPE_LABELS } from '../../../constants/statuses';
import systemTypeService from '../../../services/systemTypeService';
import { validateRequired } from '../../../utils/validators';
import { useSite } from '../../../contexts/SiteContext';
import customerService from '../../../services/customerService';
import siteService from '../../../services/siteService';

const AddTicketModal = ({ isOpen, onClose, onSubmit }) => {
  // Check if we are inside the customer context (which wraps the CustomerDashboard)
  const siteContext = useSite();
  const isCustomerContext = !!siteContext;
  const globalSelectedSite = isCustomerContext ? siteContext.selectedSite : null;

  const initialState = {
    title: '',
    description: '',
    system_type: '',
    problem_type: '', 
    custom_problem: '', 
    ticket_type: isCustomerContext ? 'AMC' : '',
    priority: 'NORMAL',
    company_id: '',
    // Customers implicitly pass their selected site. Admins must pick one manually.
    site_id: isCustomerContext ? String(globalSelectedSite) : ''
  };

  const [formData, setFormData] = useState(initialState);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [systemTroubles, setSystemTroubles] = useState({});

  // States to populate dropdowns for Admins
  const [companies, setCompanies] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);

  useEffect(() => {
    // If opened by an admin/management, pre-fetch organizations and sites
    if (isOpen && !isCustomerContext) {
      loadAdminData();
    }
  }, [isOpen, isCustomerContext]);

  useEffect(() => {
    if (isOpen && isCustomerContext && globalSelectedSite) {
      setFormData(prev => ({
        ...prev,
        site_id: String(globalSelectedSite)
      }));
    }
  }, [isOpen, globalSelectedSite, isCustomerContext]);

  useEffect(() => {
  systemTypeService.getProblemMapping().then(result => {
    if (result.success) setSystemTroubles(result.data);
  });
  }, []);

  const loadAdminData = async () => {
    setLoadingData(true);
    const [companiesRes, sitesRes] = await Promise.all([
      customerService.getCustomerCompanies(),
      siteService.getAllSites()
    ]);

    if (companiesRes.success) setCompanies(companiesRes.data);
    if (sitesRes.success) setAllSites(sitesRes.data);
    setLoadingData(false);
  };

  useEffect(() => {
    const fetchContract = async () => {
      if (!formData.site_id) {
        setActiveContract(null);
        return;
      }
      
      setLoadingContract(true);
      // We can grab the contract based on the site. Since we have `/users/{id}/active-contract` or can use a siteService endpoint. 
      // The CustomerDashboard already verified this for Customers. For Admins, we might query active contract for site.
      // Re-using the getActiveContract logic. We need the customer ID associated with the site, but we can hit contracts directly.
      try {
         const response = await siteService.getSiteContracts(formData.site_id);
         if (response.success) {
           const contracts = response.data;
           const active = contracts.find(c => c.is_enabled);
           setActiveContract(active || null);
         } else {
           setActiveContract(null);
         }
      } catch (err) {
         console.error(err);
         setActiveContract(null);
      } finally {
         setLoadingContract(false);
      }
    };
    
    fetchContract();
  }, [formData.site_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      
      if (name === 'ticket_type') {
        if (TYPES_REQUIRING_NO_SELECTION.includes(value)) {
          // Clear standard fields for PM-like tickets
          updatedData.system_type = '';
          updatedData.problem_type = '';
          updatedData.custom_problem = '';
        }
      }

      if (name === 'system_type') {
        updatedData.problem_type = '';
        updatedData.custom_problem = '';
      }
      
      if (name === 'problem_type' && value !== 'Other') {
        updatedData.custom_problem = '';
      }

      // If Admin switches the company, reset the site selection
      if (name === 'company_id') {
        updatedData.site_id = '';
      }

      return updatedData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = async (file) => {
    setAttachments(prev => [...prev, {
      file: file,
      name: file.name,
      size: file.size,
      type: file.type
    }]);
  };

  const handleRemoveFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType) => {
    return ''; // Placeholder
  };

  const validate = () => {
    const newErrors = {};
    
    const isPM = TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type);
    
    if (!validateRequired(formData.title)) newErrors.title = 'Title is required';
    
    if (!isPM) {
      if (!validateRequired(formData.system_type)) newErrors.system_type = 'System type is required';
      if (!validateRequired(formData.problem_type)) newErrors.problem_type = 'Problem type is required';
      else if (formData.problem_type === 'Other' && !validateRequired(formData.custom_problem)) {
        newErrors.custom_problem = 'Please specify your custom problem';
      }
    }
    if (!validateRequired(formData.priority)) newErrors.priority = 'Priority is required';
    if (!validateRequired(formData.ticket_type)) newErrors.ticket_type = 'Ticket type is required';

    // Admins have strict validations for these hidden-for-customer fields
    if (!isCustomerContext) {
      if (!validateRequired(formData.company_id)) newErrors.company_id = 'Company is required';
      if (!validateRequired(formData.site_id)) newErrors.site_id = 'Site is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      // Clean payload: ensure correct datatypes
      const payload = { ...formData };
      if (!isCustomerContext) {
        payload.company_id = parseInt(formData.company_id, 10);
        payload.site_id = parseInt(formData.site_id, 10);
      }

      await onSubmit(payload, attachments);
      setFormData(initialState);
      setAttachments([]);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialState);
      setAttachments([]);
      setErrors({});
      onClose();
    }
  };

  // Systems and Priorities logic
  const systemTypeOptions = Object.keys(systemTroubles)
    .filter(type => {
      if (!activeContract || !activeContract.services) return false;
      const services = activeContract.services;
      if (Array.isArray(services)) return services.includes(type);
      return (services.comprehensive || []).includes(type) || (services.non_comprehensive || []).includes(type);
    })
    .map(type => ({
      value: type,
      label: type
    }));

  let problemTypeOptions = [];
  if (formData.system_type && systemTroubles[formData.system_type]) {
    problemTypeOptions = systemTroubles[formData.system_type].map(problem => ({
      value: problem,
      label: problem
    }));
    problemTypeOptions.push({ value: 'Other', label: 'Other (Specify)' });
  }

  const priorityOptions = Object.values(TICKET_PRIORITIES).map(p => ({
    value: p, label: p
  }));

  const ticketTypeOptions = Object.values(TICKET_TYPES).map(t => ({
    value: t, label: TICKET_TYPE_LABELS[t] || t
  }));

  // Dynamic dropdowns for Admin
  const adminCompanyOptions = [
    { value: '', label: '-- Select Company --' },
    ...companies.map(c => ({ value: c.id.toString(), label: c.company_name }))
  ];

  const adminSiteOptions = [
    { value: '', label: '-- Select Site --' },
    ...allSites
      .filter(s => s.company_id === parseInt(formData.company_id, 10))
      .map(s => ({ value: s.id.toString(), label: s.name || s.site_name }))
  ];

  // Styles
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    marginTop: '20px'
  };

  const attachmentListStyle = { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' };
  const attachmentItemStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--surface-light)', borderRadius: '8px', border: '1px solid var(--border)' };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>Cancel</Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Creating...' : 'Create Ticket'}</Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Ticket" size="large" footer={footer}>
      <form onSubmit={handleSubmit}>
        {/* Dynamic assignment controls restricted to Admins. MOVED TO TOP as requested */}
        {!isCustomerContext && (
          <div style={gridStyle}>
            <Select 
              label="Company" 
              name="company_id" 
              value={formData.company_id} 
              onChange={handleChange} 
              options={adminCompanyOptions} 
              error={errors.company_id} 
              disabled={loadingData} 
              required 
            />
            <Select 
              label="Site" 
              name="site_id" 
              value={formData.site_id} 
              onChange={handleChange} 
              options={adminSiteOptions} 
              error={errors.site_id} 
              disabled={!formData.company_id || loadingData} 
              required 
            />
          </div>
        )}

        <Input label="Ticket Title" name="title" placeholder="e.g., HVAC System Not Working" value={formData.title} onChange={handleChange} error={errors.title} required />
        <Textarea label="Description" name="description" placeholder="Provide detailed information about the issue..." value={formData.description} onChange={handleChange} rows={4} />

        <div style={gridStyle}>
          <Select 
            label="Ticket Type" 
            name="ticket_type" 
            value={formData.ticket_type} 
            onChange={handleChange} 
            options={isCustomerContext ? [
              { value: 'AMC', label: TICKET_TYPE_LABELS[TICKET_TYPES.AMC] || 'AMC Complaint Call' },
              { value: 'PM', label: TICKET_TYPE_LABELS[TICKET_TYPES.PM] || 'PPM Activity' }
            ] : ticketTypeOptions} 
            error={errors.ticket_type} 
            required 
            placeholder="Select ticket type" 
          />
          <Select 
            label="Priority" 
            name="priority" 
            value={formData.priority} 
            onChange={handleChange} 
            options={priorityOptions} 
            error={errors.priority} 
            required 
          />
        </div>

        <div style={gridStyle}>
          <Select 
            label={loadingContract ? "Loading allowed systems..." : "System Type"} 
            name="system_type" 
            value={formData.system_type} 
            onChange={handleChange} 
            options={systemTypeOptions} 
            error={errors.system_type} 
            required={!TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
            disabled={!formData.site_id || loadingContract || !activeContract || TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
            placeholder={TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type) ? "-- Not required for PM --" : !formData.site_id ? "-- Select a site first --" : (!activeContract ? "No active contract found" : "-- Select system type --")} 
          />
          <Select 
            label="Sub-Type (Problem)" 
            name="problem_type" 
            value={formData.problem_type} 
            onChange={handleChange} 
            options={problemTypeOptions} 
            error={errors.problem_type} 
            required={!TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)}
            disabled={!formData.system_type || TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type)} 
            placeholder={TYPES_REQUIRING_NO_SELECTION.includes(formData.ticket_type) ? "-- Not required for PM --" : !formData.system_type ? "Select a system type first" : "-- Select problem --"} 
          />
        </div>

        {formData.problem_type === 'Other' && (
          <div style={gridStyle}>
            <Input label="Specify Custom Problem" name="custom_problem" placeholder="Enter specific problem details" value={formData.custom_problem} onChange={handleChange} error={errors.custom_problem} required />
          </div>
        )}

          {/* Section was moved up to follow priority */}

        <div style={sectionTitleStyle}> Attachments (Optional)</div>
        <FileUpload onUpload={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" maxSize={10485760} multiple={false} />

        {attachments.length > 0 && (
          <div style={attachmentListStyle}>
            {attachments.map((attachment, index) => (
              <div key={index} style={attachmentItemStyle}>
                <div style={{ fontSize: '32px', flexShrink: 0 }}>{getFileIcon(attachment.type)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {attachment.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{formatFileSize(attachment.size)}</div>
                </div>
                <Button variant="ghost" size="small" onClick={() => handleRemoveFile(index)}>Remove</Button>
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: '16px', background: 'var(--surface-light)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', marginTop: '16px', border: '1px solid var(--border)' }}>
           <strong>Note:</strong> Attachments will be uploaded after the ticket is created. You can add more files later.
        </div>
      </form>
    </Modal>
  );
};

export default AddTicketModal;